import { getListOptions } from '@common/services';
import { IDatabaseColumn, IMultiSelect, IObj } from '@common/types/';
import { AxiosResponse } from 'axios';
import { ceil, find, get, isEmpty, isNil, map } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const SELECT_ALL = 'SELECT_ALL';
const PAGE_SIZE = 100;
const useMultipleCheckbox = (attributes: IMultiSelect | any) => {
  const { changeInput, valueInput, dependencies } = attributes;
  const [listOption, setListOption] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [hasLoadMore, setHasLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pageSize = get(attributes, 'field.pageSize', PAGE_SIZE);
  const totalPage = useMemo(() => ceil(total / pageSize), [total]);

  const onLoadMore = useCallback(() => {
    if (page < totalPage) {
      setPage((preState) => preState + 1);
    }
  }, [page, totalPage]);

  const getLabelField = get(attributes, 'database.options.labelField');
  const isMakeCustom = get(attributes, 'seperate.enabled', false);

  const fieldTable: any[] = [];

  const getFieldTable = find(
    fieldTable,
    (item) => item.databaseUuid === attributes.databaseUuid
  );

  const getFieldId: IDatabaseColumn | any = find(
    getFieldTable?.fields,
    (item) => item.fid === getLabelField
  );

  const optionDataOfDatabase = useMemo(() => {
    const defaultValue =
      get(attributes, 'attributes.defaultValue.text') ||
      get(attributes, 'database.options.labelField');

    return map(listOption, (value: any) => {
      return {
        value: value._id || value.id,
        label: value?.[defaultValue],
      };
    }).filter(
      (item: Record<string, any>) => !isNil(item.label) || !isEmpty(item.label)
    );
  }, [listOption, getFieldTable, getFieldId]);

  const optionCustomData = useMemo(() => {
    return get(attributes, 'seperate.items', '')
      .split('\n')
      .map((item: string) => ({
        label: item.trim(),
        value: item.trim(),
      }))
      .filter(
        (item: Record<string, any>) =>
          !isNil(item.label) || !isEmpty(item.label)
      );
  }, [attributes]);

  const list = isMakeCustom ? optionCustomData : optionDataOfDatabase;

  const handleChangeValue = (value: string | number) => {
    const currentValues = valueInput || [];
    const isSelected = currentValues.includes(value);

    if (value === SELECT_ALL) {
      if (isSelected || currentValues.length === list.length) {
        changeInput([]);
      } else {
        changeInput(list.map((item: { value: any }) => item.value));
      }
      return;
    }

    let newValues: (string | number)[];

    if (isSelected) {
      newValues = currentValues.filter((v: string | number) => v !== value);
    } else {
      if (
        attributes.maxSelections &&
        currentValues.length >= attributes.maxSelections
      ) {
        return;
      }
      newValues = [...currentValues, value];
    }

    changeInput(newValues);
  };

  const clearAll = () => {
    changeInput([]);
  };

  const isAllSelected = useMemo(() => {
    if (!list.length || !valueInput?.length) return false;
    return list.every((item: { value: any }) =>
      valueInput.includes(item.value)
    );
  }, [list, valueInput]);

  const selectedCount = valueInput?.length || 0;

  useEffect(() => {
    const getListOption = async (field: IObj) => {
      const params = {
        appId: dependencies.appInfor.id,
        fieldId: field?.fieldId,
        screenUuid: attributes?.screenUuid,
        objectId: attributes?.formId,
        dependencies,
        limit: pageSize * page,
      };

      setIsLoading(true);
      getListOptions(params)
        .then((resp: AxiosResponse) => {
          setListOption(resp.data);
          setTotal(get(resp, 'total'));
          if (get(resp, 'data.length') < get(resp, 'total')) {
            setHasLoadMore(true);
          } else {
            setHasLoadMore(false);
          }
        })
        .catch(() => {
          setListOption([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (attributes?.formId && attributes?.field && dependencies?.appInfor) {
      getListOption(attributes?.field);
    } else {
      setListOption(attributes.dataBinding || []);
    }
  }, [
    attributes.formId,
    attributes.field,
    dependencies.appInfor,
    attributes.dataBinding,
    page,
  ]);

  return {
    value: valueInput || [],
    list,
    handleChangeValue,
    onLoadMore,
    hasLoadMore,
    isLoading,
    isAllSelected,
    selectedCount,
    clearAll,
  };
};

export type useMultipleCheckboxProps = ReturnType<typeof useMultipleCheckbox>;
export default useMultipleCheckbox;
